import React, { ChangeEvent, ReactNode, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { useState } from 'react';
import { OrderInstruction } from 'components/maps/BundleMap';
import { BundleJson, InstructionType } from 'types/bundleTypes';
import clsx from 'clsx';
import {
  FaCheck,
  FaGripVertical,
  FaRegTimesCircle,
  FaSortDown,
  FaSortUp,
  FaTimes,
} from 'react-icons/fa';
import {
  Checkbox,
  Link,
  TableCell,
  TableFooter,
  Tooltip,
} from '@material-ui/core';
import { formatOrderWindowInterval } from 'utils/date';
import { createGroupColors } from 'pages/oldPlanner/planner-map/map/PlannerMapMapUtils';
import { formatTime } from 'utils/bundlesMonitorUtils';
import dayjs from 'dayjs';
import { TooltipComponent } from 'ReusableComponents/Tooltip/TooltipComponent';
import { InstructionGroup } from 'pages/monitor/MonitorLive/Monitor.types';
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot,
  DropResult,
  ResponderProvided,
} from 'react-beautiful-dnd';
import arrayMove from 'array-move';
import { Alert } from '@material-ui/lab';
import Button from 'components/buttons/Button';
import {
  setBackgroundColorByStatus,
  setColorByStatus,
} from 'pages/bundles/components/BundleUtils';
import { InputComponent } from 'ReusableComponents/Input/InputComponent';
import './OrderBundle.scss';
import { getBundleEtaDelay } from './../../utils/bundlesMonitorUtils';
import { Prompt } from 'react-router-dom';
import { useBeforeunload } from 'react-beforeunload';

export enum OrderBundleColumn {
  InstructionType = 'Type',
  OrderNumber = 'Order',
  Pin = 'Pin',
  Address = 'Address',
  LocationAddress = 'Address/Name',
  Customer = 'Customer',
  TemperatureOrder = 'Temp',
  WindowTime = 'Window',
  Diff = 'Diff',
  EstimateTimeArrival = 'ETA',
  NewEstimateTimeArrival = 'New ETA',
  Time = 'Time',
  Done = 'Done',
  OperationType = 'Type',
  Status = 'Status',
}

const useItemStyle = makeStyles(theme => ({
  dnd: {
    maxWidth: 16,
  },
  checkboxWrapper: {
    maxWidth: 20,
  },
  checkbox: {
    padding: 0,
    display: 'block',
  },
  pickupBg: {
    backgroundColor: '#fff80f',
    color: 'black',
  },
  deliveryBg: {
    backgroundColor: '#72b46e',
    color: 'white',
  },
  orderNumber: {
    '& a': {
      color: theme.palette.info.main,
      textDecoration: 'none',
    },
    '& svg': {
      '&.check': {
        color: theme.palette.success.main,
      },
      fontSize: '7px',
      position: 'relative',
      right: '-1px',
    },
  },
  eta: {
    '& .disabled': {
      color: theme.palette.grey[400],
    },
  },
  delete: {
    '& svg:hover': {
      color: theme.palette.error.main,
    },
    cursor: 'pointer',
    fontSize: '1em',
    verticalAlign: 'middle',
    lineHeight: '1em',
  },
  search: {
    marginTop: 0,
    marginBottom: 0,
  },
  address: {
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

const useItemRowStyle = makeStyles(theme => ({
  root: {
    '&.border': {
      '& td': {
        borderRight: `1px solid ${theme.palette.grey[300]}`,
      },
    },
    '& td, th': {
      '&:last-child': {
        borderRight: 'none',
        paddingRight: 4,
      },
      '&.multi-line': {
        whiteSpace: 'pre-line',
      },
      paddingRight: 4,
      paddingLeft: 4,
      whiteSpace: 'nowrap',
    },
    '& th': {
      fontWeight: 'bold',
    },

    backgroundColor: 'white',
  },
  foot: {
    '& td': {
      '&:last-child': {
        paddingRight: 0,
      },
      padding: 0,
    },
  },
}));

const useContentStyle = makeStyles(theme => ({
  wrapper: {
    '&.border': {
      borderLeft: `1px solid ${theme.palette.grey[400]}`,
      borderRight: `1px solid ${theme.palette.grey[400]}`,
    },
  },
}));

export interface OrderBundleProps {
  border?: boolean;
  hasDragAndDrop?: boolean;
  hasCheckbox?: boolean;
  hasDelete?: boolean;
  hasSearch?: boolean;
  defaultOrderInstructions: OrderInstruction[];
  columns: Array<keyof typeof OrderBundleColumn>;
  completedStatusList?: boolean;
  pickupsGroup?: InstructionGroup;
  bundle: BundleJson;
  selectedOrders?: string[];
  onSelectOrderInstruction?: (
    orderInstructions: Array<OrderInstruction>
  ) => void;
  onDeleteOrderInstruction?: (orderId: string) => void;
  onSaveDndDraft?: (
    bundle: BundleJson,
    newOrderInstructions: Array<OrderInstruction>
  ) => void;
}

export const OrderBundle: React.FunctionComponent<OrderBundleProps> = ({
  border = false,
  hasDragAndDrop = false,
  hasCheckbox = false,
  hasDelete = false,
  hasSearch = false,
  defaultOrderInstructions,
  columns = [],
  completedStatusList = false,
  pickupsGroup,
  bundle,
  selectedOrders,
  onSelectOrderInstruction,
  onDeleteOrderInstruction,
  ...props
}: OrderBundleProps) => {
  const itemStyles = useItemStyle();
  const itemRowStyle = useItemRowStyle();
  const contentStyle = useContentStyle();

  const [orderInstructions, setOrderInstructions] = useState<
    Array<OrderInstruction>
  >([...defaultOrderInstructions]);
  const [selectedInstructions, setSelectedInstructions] = useState<
    Array<OrderInstruction>
  >([]);
  const [selectAllInstruction, setSelectAllInstruction] =
    useState<boolean>(false);
  const [showPickupGroup, setShowPickupGroup] = useState<boolean>(false);
  const [searchKeyword, setSearchKeyword] = useState<string>('');

  const isDndDraft =
    JSON.stringify(defaultOrderInstructions) !==
    JSON.stringify(orderInstructions);

  /* This part is used for showing the Browser Warning.
   * This will be triggered whenever there is unsaved Drag&Drop and user refresh page.
   * Default message : `Changes that you made may not be saved.`
   * Unfortunately, the message cannot be changed
   * Ref : https://stackoverflow.com/a/40570214/4112654 */
  useBeforeunload((evt: Event) => {
    if (isDndDraft) {
      evt.preventDefault();
    }
  });

  useEffect(() => {
    const selectedOrdersResetFromOutside =
      selectedOrders && selectedOrders.length <= 0;

    if (!selectedOrders || selectedOrdersResetFromOutside) {
      /* Reset checkbox everytime the feature is Toggled */
      setSelectedInstructions([]);
    }
  }, [hasCheckbox, selectedOrders]);

  useEffect(() => {
    /* Reset the list to use the default value whenever there is external changes */
    setOrderInstructions([...defaultOrderInstructions]);
  }, [defaultOrderInstructions]);

  const pin = (orderInstruction: OrderInstruction): ReactNode => {
    const order = orderInstruction.order;
    const type = orderInstruction.instruction.type;
    switch (type) {
      case InstructionType.Delivery:
        return (
          <span className="hidden">
            <span>Order ID {order.id} Delivery Pin : </span>
            <span>{order.confirmationPin}</span>
          </span>
        );
      case InstructionType.Pickup:
        return <span>{order.pickupPin}</span>;
      default:
        return <span></span>;
    }
  };

  const address = (orderInstruction: OrderInstruction): string => {
    const order = orderInstruction.order;
    const type = orderInstruction.instruction.type;

    if (!order) {
      return '...';
    }

    if (type === InstructionType.Pickup) {
      return `${order.senderName || ''}, ${order.originAddress.postalCode} ${
        order.originAddress.city
      }`;
    }

    return order.destinationAddress?.fullAddressString;
  };

  const locationAddress = (orderInstruction: OrderInstruction): string => {
    const order = orderInstruction.order;
    const type = orderInstruction.instruction.type;

    if (!order) {
      return '...';
    }

    if (type === InstructionType.Pickup) {
      /* Return data as multi-line */
      return `${order.originAddress.fullAddressString}
      ${order.pickupName}
      ${order.pickupPhone}`;
    }

    /* Return data as multi-line */
    return `${order.recipientAddress}
    ${order.recipientName}
    ${order.recipientPhone}`;
  };

  const getInstructionTime = (
    orderInstruction: OrderInstruction
  ): dayjs.Dayjs | null => {
    const order = orderInstruction.order;
    const instruction = orderInstruction.instruction;
    const type = instruction.type;
    let timeVal: string;
    if (type === InstructionType.Delivery) {
      timeVal = order.deliveredAt;
    } else if (type === InstructionType.Pickup) {
      timeVal = order?.pickedAt;
    }
    return timeVal ? dayjs(timeVal) : null;
  };

  const getInstructionDiff = (
    orderInstruction: OrderInstruction,
    instructionTime: dayjs.Dayjs | null
  ): string => {
    let diff = '-';
    if (
      instructionTime &&
      orderInstruction.order &&
      orderInstruction.instruction.eta.isValid()
    ) {
      diff = dayjs(orderInstruction.instruction.eta)
        .diff(instructionTime, 'minute')
        .toString();
    }

    return diff;
  };

  const windowTime: Array<string> = orderInstructions.map(orderInstruction => {
    const order = orderInstruction.order;
    return order ? formatOrderWindowInterval(order) : '00:00 - 00:00';
  });
  const windowColors = createGroupColors(windowTime);

  const etaDelay = getBundleEtaDelay(orderInstructions);

  const newEta = (
    orderInstruction: OrderInstruction,
    orderInstructionIndex: number
  ) => {
    const instruction = orderInstruction.instruction;
    return etaDelay &&
      instruction.eta &&
      orderInstructionIndex > etaDelay.lastDeliveredIdx &&
      etaDelay.delayMinutes !== 0
      ? formatTime(instruction.eta.add(-etaDelay.delayMinutes, 'minute'))
      : '-';
  };

  const orderBundleColumns: Array<keyof typeof OrderBundleColumn> = Object.keys(
    OrderBundleColumn
  ).filter(
    column =>
      isNaN(Number(column)) &&
      columns.includes(column as keyof typeof OrderBundleColumn)
  ) as Array<keyof typeof OrderBundleColumn>;

  const onSelectAllCheckbox = () => {
    const selectAll = !selectAllInstruction;
    let selectedInstruction = [];

    if (selectAll) {
      selectedInstruction = [...orderInstructions];
      setShowPickupGroup(true);
    }

    setSelectedInstructions(selectedInstruction);
    onSelectOrderInstruction(selectedInstruction);
    setSelectAllInstruction(selectAll);
  };

  const isSelectedInstructions = (currentInstruction: OrderInstruction) => {
    const instructionIndex = selectedInstructions.findIndex(
      (existingInstruction: OrderInstruction) =>
        existingInstruction.label === currentInstruction.label
    );

    return instructionIndex >= 0;
  };

  const onSelectCheckbox = (selectedInstruction: OrderInstruction) => {
    const instructionIndex = selectedInstructions.findIndex(
      (existingInstruction: OrderInstruction) =>
        existingInstruction.label === selectedInstruction.label
    );

    const newSelectedInstructions = [...selectedInstructions];
    if (instructionIndex >= 0) {
      /* Remove selected */
      newSelectedInstructions.splice(instructionIndex, 1);
    } else {
      /* Add selected */
      newSelectedInstructions.push(selectedInstruction);
    }
    setSelectedInstructions(newSelectedInstructions);
    onSelectOrderInstruction(newSelectedInstructions);

    const isSelectAllInstruction =
      orderInstructions.length > 0 &&
      newSelectedInstructions.length === orderInstructions.length;
    setSelectAllInstruction(isSelectAllInstruction);
  };

  const onTogglePickupGroup = () => {
    setShowPickupGroup(!showPickupGroup);
  };

  const hidePickupOrder = (orderInstructionIndex: number) => {
    return (
      !showPickupGroup &&
      pickupsGroup &&
      orderInstructionIndex >= pickupsGroup.index &&
      orderInstructionIndex < pickupsGroup.index + pickupsGroup.count
    );
  };

  const moveInstruction = (
    fromIndex: number,
    destination: number
  ): Array<OrderInstruction> => {
    if (destination < 0) {
      return;
    }
    if (destination > orderInstructions.length - 1) {
      return;
    }

    const newOrderInstruction = arrayMove(
      orderInstructions,
      fromIndex,
      destination
    );

    return newOrderInstruction;
  };

  const onDragEnd = (result: DropResult) => {
    /* Dropped outside the list */
    if (!result.destination) {
      return;
    }

    const origin = result.source.index;
    const destination = result.destination.index;
    if (origin !== destination) {
      const newOrderInstruction = moveInstruction(origin, destination);
      setOrderInstructions(newOrderInstruction);
    }
  };

  const onSaveDndDraft = () => {
    props.onSaveDndDraft(bundle, orderInstructions);
  };

  const onDiscardDndDraft = () => {
    setOrderInstructions([...defaultOrderInstructions]);
  };

  const onSearchKeywordChange = (evt: ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(evt.target.value);
  };

  const isHighlighted = (orderInstruction: OrderInstruction) => {
    const orderIdMatched =
      orderInstruction.instruction.orderId.includes(searchKeyword);
    const addressMatched = address(orderInstruction)
      .toLowerCase()
      .includes(searchKeyword.toLowerCase());
    const customerMatched = orderInstruction.order.recipientName
      .toLowerCase()
      .includes(searchKeyword.toLowerCase());

    return (
      searchKeyword && (orderIdMatched || addressMatched || customerMatched)
    );
  };

  const onOrderRowClicked = (
    evt: React.MouseEvent<HTMLTableRowElement>,
    orderInstruction: OrderInstruction
  ) => {
    const clickedAreaIsLink = evt.target instanceof HTMLAnchorElement;
    const isRowSelected = hasCheckbox && !clickedAreaIsLink;

    if (isRowSelected) {
      onSelectCheckbox(orderInstruction);
    }
  };

  return (
    <TableContainer className={clsx({ border }, contentStyle.wrapper)}>
      <Prompt
        when={isDndDraft}
        message={`You have unsaved Drag&Drop action on the Order Bundle.\nAre you sure you want to leave?`}
      />
      <Table aria-label="Order Bundle Table" size="small">
        <TableHead>
          <TableRow className={itemRowStyle.root}>
            {hasDragAndDrop && (
              <TableCell variant="head" className={itemStyles.dnd} />
            )}
            {hasCheckbox && (
              <TableCell
                align="center"
                variant="head"
                className={itemStyles.checkboxWrapper}
              >
                <TooltipComponent
                  id="Select-all-order-instruction"
                  title="Select all Order Instruction"
                >
                  <Checkbox
                    disabled={completedStatusList}
                    className={itemStyles.checkbox}
                    checked={selectAllInstruction}
                    onClick={onSelectAllCheckbox}
                  />
                </TooltipComponent>
              </TableCell>
            )}
            {orderBundleColumns.map((column, columnIndex) => (
              <TableCell
                key={`header-${columnIndex}-${column}`}
                align="center"
                variant="head"
              >
                {OrderBundleColumn[column]}
              </TableCell>
            ))}
            {hasDelete && <TableCell variant="head" />}
          </TableRow>
          {hasSearch && (
            <TableRow className={itemRowStyle.root}>
              <TableCell
                colSpan={
                  columns.length +
                  ~~hasDragAndDrop +
                  ~~hasCheckbox +
                  ~~hasDelete
                }
              >
                <InputComponent
                  fullWidth
                  size="small"
                  variant="outlined"
                  id="order-search-input"
                  placeholder="Search by order id / address / customer"
                  name="order-search-input"
                  label=""
                  className={itemStyles.search}
                  value={searchKeyword}
                  onChange={onSearchKeywordChange}
                />
              </TableCell>
            </TableRow>
          )}
        </TableHead>
        <DragDropContext
          onDragEnd={(result: DropResult, _: ResponderProvided) =>
            onDragEnd(result)
          }
        >
          <Droppable droppableId="order-bundle">
            {(
              providedDropable: DroppableProvided,
              _: DroppableStateSnapshot
            ) => (
              <TableBody
                {...providedDropable.droppableProps}
                ref={providedDropable.innerRef}
              >
                {orderInstructions.length <= 0 && (
                  <TableRow className={itemRowStyle.root} key="no-data">
                    <TableCell
                      colSpan={
                        columns.length +
                        ~~hasDragAndDrop +
                        ~~hasCheckbox +
                        ~~hasDelete
                      }
                    >
                      No data.
                    </TableCell>
                  </TableRow>
                )}
                {orderInstructions.map(
                  (orderInstruction, orderInstructionIndex) => {
                    const instructionTime =
                      getInstructionTime(orderInstruction);
                    return (
                      <React.Fragment key={`order-${orderInstructionIndex}`}>
                        {pickupsGroup &&
                          pickupsGroup.index === orderInstructionIndex && (
                            <TableRow
                              className={itemRowStyle.root}
                              key={`collapsible-row-${orderInstructionIndex}`}
                              onClick={onTogglePickupGroup}
                            >
                              {hasDragAndDrop && (
                                <TableCell className={itemStyles.dnd} />
                              )}
                              {hasCheckbox && (
                                <TableCell
                                  className={itemStyles.checkboxWrapper}
                                />
                              )}
                              <TableCell
                                align="center"
                                className={itemStyles.pickupBg}
                              >
                                P
                              </TableCell>
                              <TableCell align="center">
                                {hidePickupOrder(orderInstructionIndex) ? (
                                  <FaSortDown />
                                ) : (
                                  <FaSortUp />
                                )}
                              </TableCell>
                              {orderBundleColumns.includes('Pin') && (
                                <TableCell />
                              )}
                              <TableCell>{pickupsGroup.address}</TableCell>
                              <TableCell
                                colSpan={
                                  ~~orderBundleColumns.includes('Customer') +
                                  ~~orderBundleColumns.includes(
                                    'TemperatureOrder'
                                  ) +
                                  ~~orderBundleColumns.includes('WindowTime') +
                                  ~~orderBundleColumns.includes('Diff') +
                                  ~~orderBundleColumns.includes(
                                    'EstimateTimeArrival'
                                  ) +
                                  ~~orderBundleColumns.includes(
                                    'NewEstimateTimeArrival'
                                  ) +
                                  ~~orderBundleColumns.includes('Time') +
                                  ~~orderBundleColumns.includes('Done') +
                                  ~~orderBundleColumns.includes(
                                    'OperationType'
                                  ) +
                                  ~~orderBundleColumns.includes('Status') +
                                  ~~hasDelete
                                }
                              >
                                Orders : {pickupsGroup.count}
                              </TableCell>
                            </TableRow>
                          )}
                        {!hidePickupOrder(orderInstructionIndex) && (
                          <Draggable
                            key={orderInstruction.label}
                            draggableId={`${orderInstruction.label}`}
                            index={orderInstructionIndex}
                            isDragDisabled={!hasDragAndDrop}
                          >
                            {(
                              provided: DraggableProvided,
                              _: DraggableStateSnapshot
                            ) => (
                              <TableRow
                                hover
                                key={`${orderInstruction.label}-${orderInstructionIndex}`}
                                className={clsx(
                                  {
                                    border,
                                    highlight: isHighlighted(orderInstruction),
                                  },
                                  itemRowStyle.root
                                )}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                onClick={evt => {
                                  onOrderRowClicked(evt, orderInstruction);
                                }}
                              >
                                {hasDragAndDrop && (
                                  <TableCell
                                    align="center"
                                    className={itemStyles.dnd}
                                  >
                                    <FaGripVertical />
                                  </TableCell>
                                )}
                                {hasCheckbox && (
                                  <TableCell
                                    align="center"
                                    className={itemStyles.checkboxWrapper}
                                  >
                                    <Checkbox
                                      disabled={completedStatusList}
                                      className={itemStyles.checkbox}
                                      checked={isSelectedInstructions(
                                        orderInstruction
                                      )}
                                      onClick={() =>
                                        onSelectCheckbox(orderInstruction)
                                      }
                                    />
                                  </TableCell>
                                )}
                                {orderBundleColumns.includes(
                                  'InstructionType'
                                ) && (
                                  <TableCell
                                    align="center"
                                    className={clsx(
                                      {
                                        [itemStyles.pickupBg]:
                                          orderInstruction.instruction.type ===
                                          InstructionType.Pickup,
                                        [itemStyles.deliveryBg]:
                                          orderInstruction.instruction.type ===
                                          InstructionType.Delivery,
                                      },
                                      'no-highlight'
                                    )}
                                  >
                                    {orderInstruction.label}
                                  </TableCell>
                                )}
                                {orderBundleColumns.includes('OrderNumber') && (
                                  <TableCell
                                    align="center"
                                    className={itemStyles.orderNumber}
                                  >
                                    <Link
                                      component="a"
                                      href={`/orders/${orderInstruction.instruction.orderId}`}
                                      target="_blank"
                                      rel="noopener"
                                    >
                                      {orderInstruction.instruction.orderId}
                                    </Link>
                                    {orderInstruction.instruction.completed && (
                                      <FaCheck className="check" />
                                    )}
                                  </TableCell>
                                )}
                                {orderBundleColumns.includes('Pin') && (
                                  <TableCell align="center">
                                    {pin(orderInstruction)}
                                  </TableCell>
                                )}
                                {orderBundleColumns.includes('Address') && (
                                  <TableCell align="left">
                                    <Tooltip title={address(orderInstruction)}>
                                      <div className={itemStyles.address}>
                                        {address(orderInstruction)}
                                      </div>
                                    </Tooltip>
                                  </TableCell>
                                )}
                                {orderBundleColumns.includes(
                                  'LocationAddress'
                                ) && (
                                  <TableCell
                                    align="left"
                                    className="multi-line"
                                  >
                                    {locationAddress(orderInstruction)}
                                  </TableCell>
                                )}
                                {orderBundleColumns.includes('Customer') && (
                                  <TableCell align="center">
                                    {orderInstruction.order.recipientName}
                                  </TableCell>
                                )}
                                {orderBundleColumns.includes(
                                  'TemperatureOrder'
                                ) && (
                                  <TableCell align="center">
                                    {orderInstruction.order
                                      .temperatureControlled
                                      ? 'Yes'
                                      : 'No'}
                                  </TableCell>
                                )}
                                {orderBundleColumns.includes('WindowTime') && (
                                  <TableCell
                                    align="center"
                                    className="no-highlight"
                                    style={{
                                      backgroundColor: windowColors.get(
                                        windowTime[orderInstructionIndex]
                                      ),
                                    }}
                                  >
                                    {windowTime[orderInstructionIndex]}
                                  </TableCell>
                                )}
                                {orderBundleColumns.includes('Diff') && (
                                  <TableCell
                                    align="center"
                                    style={{
                                      backgroundColor:
                                        orderInstruction.instruction.eta.isValid() &&
                                        orderInstruction.order &&
                                        instructionTime
                                          ? setBackgroundColorByStatus(
                                              orderInstruction.instruction.eta,
                                              instructionTime
                                            )
                                          : 'unset',
                                      color:
                                        orderInstruction.instruction.eta.isValid() &&
                                        orderInstruction.order &&
                                        instructionTime
                                          ? setColorByStatus(
                                              orderInstruction.instruction.eta,
                                              instructionTime
                                            )
                                          : 'unset',
                                    }}
                                  >
                                    {getInstructionDiff(
                                      orderInstruction,
                                      instructionTime
                                    )}
                                  </TableCell>
                                )}
                                {orderBundleColumns.includes(
                                  'EstimateTimeArrival'
                                ) && (
                                  <TableCell
                                    align="center"
                                    className={itemStyles.eta}
                                  >
                                    {orderInstruction.instruction.eta ? (
                                      <span>
                                        {formatTime(
                                          orderInstruction.instruction.eta
                                        )}
                                      </span>
                                    ) : (
                                      <span className="disabled">No ETA</span>
                                    )}
                                  </TableCell>
                                )}
                                {orderBundleColumns.includes(
                                  'NewEstimateTimeArrival'
                                ) && (
                                  <TableCell align="center">
                                    {newEta(
                                      orderInstruction,
                                      orderInstructionIndex
                                    )}
                                  </TableCell>
                                )}
                                {orderBundleColumns.includes('Time') && (
                                  <TableCell align="center">
                                    {getInstructionTime(
                                      orderInstruction
                                    )?.format('HH:mm') || '-'}
                                  </TableCell>
                                )}
                                {orderBundleColumns.includes('Done') && (
                                  <TableCell align="center">
                                    {getInstructionTime(
                                      orderInstruction
                                    )?.format('HH:mm') || '-'}
                                  </TableCell>
                                )}
                                {orderBundleColumns.includes(
                                  'OperationType'
                                ) && (
                                  <TableCell align="center">
                                    {
                                      Object.keys(InstructionType)[
                                        Object.values(InstructionType).indexOf(
                                          orderInstruction.instruction.type
                                        )
                                      ]
                                    }
                                  </TableCell>
                                )}
                                {orderBundleColumns.includes('Status') && (
                                  <TableCell align="center">
                                    {orderInstruction.instruction.completed
                                      ? 'Completed'
                                      : 'In progress'}
                                  </TableCell>
                                )}
                                {hasDelete && (
                                  <TableCell
                                    align="center"
                                    className={itemStyles.delete}
                                  >
                                    <TooltipComponent
                                      id={`Remove-order-${orderInstruction.instruction.orderId}`}
                                      title={`Remove ${orderInstruction.instruction.orderId} from Bundle`}
                                    >
                                      <FaRegTimesCircle
                                        onClick={() =>
                                          onDeleteOrderInstruction(
                                            orderInstruction.instruction.orderId
                                          )
                                        }
                                      />
                                    </TooltipComponent>
                                  </TableCell>
                                )}
                              </TableRow>
                            )}
                          </Draggable>
                        )}
                      </React.Fragment>
                    );
                  }
                )}
                {providedDropable.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>
        {isDndDraft && (
          <TableFooter>
            <TableRow className={itemRowStyle.foot}>
              <TableCell
                colSpan={
                  orderBundleColumns.length +
                  ~~hasCheckbox +
                  ~~hasDelete +
                  ~~hasDragAndDrop
                }
              >
                <Alert
                  severity="info"
                  action={
                    <>
                      <Button
                        title="Save changes"
                        style={{ fontSize: 'inherit' }}
                        onClick={onSaveDndDraft}
                      />
                      <TooltipComponent
                        id="Discard-changes"
                        title="Discard changes"
                      >
                        <FaTimes onClick={onDiscardDndDraft} />
                      </TooltipComponent>
                    </>
                  }
                >
                  Order in Bundle {bundle.id} was changed…
                </Alert>
              </TableCell>
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
};
